/**
 * MH-03 Chi tiết thiết bị — bốn tab đúng mục 4.2 đặc tả giao diện:
 * thông tin chung, trạng thái, nhật ký, bảo trì.
 *
 * Tab trạng thái đọc dữ liệu THÔ khi khoảng thời gian dưới 48 giờ, TỔNG HỢP THEO
 * GIỜ từ 48 giờ, TỔNG HỢP THEO NGÀY từ 90 ngày (Bảng 12.1), và luôn nêu rõ độ
 * phân giải đang hiển thị để người dùng không hiểu sai đường biểu đồ.
 */
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { thietBiApi, type ChiTietThietBi } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong, TrangThaiRongLoc } from '../thanh-phan/TrangThaiManHinh';
import { NhanThoiDiem, NhanTrangThai } from '../thanh-phan/NhanTrangThai';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { MH08NganDieuKhien } from './MH08NganDieuKhien';
import { TaiKhoanKetNoiThietBi } from './TaiKhoanKetNoiThietBi';

type TenTab = 'chung' | 'trangThai' | 'nhatKy' | 'baoTri';

const KHOANG = [
  { ma: '24h', nhan: vi.chiTiet.gio24, giay: 24 * 3600 },
  { ma: '7d', nhan: vi.chiTiet.ngay7, giay: 7 * 86_400 },
  { ma: '90d', nhan: vi.chiTiet.ngay90, giay: 90 * 86_400 },
] as const;

export function MH03ChiTietThietBi(): ReactNode {
  const { id = '' } = useParams();
  const phien = dungPhien();
  const [tab, datTab] = useState<TenTab>('chung');
  const [moNgan, datMoNgan] = useState(false);

  const kq = dungTaiDuLieu<ChiTietThietBi>({
    tai: (signal) => thietBiApi.chiTiet(id, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
  });

  if (kq.trangThai === 'dang-tai') return <KhungXuong soDong={6} soCot={2} taiLau={kq.taiLau} />;
  if (kq.trangThai === 'loi' && kq.loi) return <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} />;
  const tb = kq.duLieu;
  if (!tb) return <TrangThaiRong moTa={vi.trangThaiManHinh.rongMoTa} />;

  const ct = tb.chiTiet as Record<string, unknown>;

  return (
    <section className="man-hinh" data-man-hinh="MH-03">
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.thietBi}>
        <Link to="/thiet-bi">{vi.chiTiet.ve}</Link> / <span>{tb.tenThietBi ?? tb.cumLoaId}</span>
      </nav>
      <header className="man-hinh__dau">
        <h1>{tb.tenThietBi ?? tb.cumLoaId}</h1>
        <p className="man-hinh__trang-thai">
          <NhanTrangThai ma={tb.trangThaiHienThi} /> —{' '}
          <NhanThoiDiem epochGiay={tb.capNhatLuc} tuoiGiay={tb.tuoiDuLieuGiay} />
        </p>
        <div className="man-hinh__hanh-dong">
          {phien.coQuyen('device.control') ? (
            <button type="button" className="nut nut--chinh" onClick={() => datMoNgan(true)}>
              {vi.thietBi.dieuKhienNhanh}
            </button>
          ) : null}
          {phien.coQuyen('device.update') ? (
            <Link className="nut" to={`/thiet-bi/${tb.id}/sua`}>
              {vi.chiTiet.sua}
            </Link>
          ) : null}
        </div>
      </header>

      <div className="tab" role="tablist" aria-label={vi.chiTiet.tab.chung}>
        {(
          [
            ['chung', vi.chiTiet.tab.chung],
            ['trangThai', vi.chiTiet.tab.trangThai],
            ['nhatKy', vi.chiTiet.tab.nhatKy],
            ['baoTri', vi.chiTiet.tab.baoTri],
          ] as [TenTab, string][]
        ).map(([ma, nhan]) => (
          <button
            key={ma}
            type="button"
            role="tab"
            id={`tab-${ma}`}
            aria-selected={tab === ma}
            aria-controls={`bang-${ma}`}
            className={tab === ma ? 'tab__nut tab__nut--dang-chon' : 'tab__nut'}
            onClick={() => datTab(ma)}
          >
            {nhan}
          </button>
        ))}
      </div>

      {tab === 'chung' ? (
        <div role="tabpanel" id="bang-chung" aria-labelledby="tab-chung" className="the-thuoc-tinh">
          <dl>
            <dt>{vi.chiTiet.maThietBi}</dt>
            <dd>
              <code>{tb.cumLoaId}</code>
            </dd>
            <dt>{vi.chiTiet.diaBan}</dt>
            <dd>
              {tb.tenThon ?? tb.maThon ?? '—'}, {tb.tenXa ?? ''} (<code>{tb.nguonId}</code>)
            </dd>
            <dt>{vi.chiTiet.nhaCungCap}</dt>
            <dd>{tb.tenNhaCungCap}</dd>
            <dt>{vi.chiTiet.phuongThucKetNoi}</dt>
            <dd>
              <span className={`huy-hieu huy-hieu--${tb.phuongThucKetNoi.replace(/_/g, '-')}`}>
                {vi.thietBi.ketNoi[tb.phuongThucKetNoi]}
              </span>
            </dd>
            {tb.phuongThucKetNoi === 'gian_tiep' ? (
              <>
                <dt>{vi.chiTiet.boTuongThich}</dt>
                <dd>{String(ct.adapterUrlGoc ?? vi.chiTiet.chuaCo)}</dd>
                <dt>{vi.chiTiet.maThamChieu}</dt>
                <dd>{String(ct.externalDeviceRef ?? vi.chiTiet.chuaCo)}</dd>
              </>
            ) : null}
            <dt>{vi.chiTiet.phienBanUngDung}</dt>
            <dd>{tb.phienBanUngDung ?? vi.chiTiet.chuaCo}</dd>
            <dt>{vi.chiTiet.congSuat}</dt>
            <dd>
              {tb.congSuat === null ? vi.chiTiet.chuaCo : tb.congSuat === 1 ? vi.chiTiet.bat : vi.chiTiet.tat}
            </dd>
            <dt>{vi.chiTiet.amLuong}</dt>
            <dd>{tb.amLuong ?? vi.chiTiet.chuaCo}</dd>
            <dt>{vi.chiTiet.nhietDo}</dt>
            <dd>{tb.nhietDo === null ? vi.chiTiet.chuaCo : `${tb.nhietDo} ${vi.chiTiet.doC}`}</dd>
            <dt>{vi.chiTiet.dungLuong}</dt>
            <dd>
              {ct.dungLuongSuDungKb
                ? `${Number(ct.dungLuongSuDungKb).toLocaleString('vi-VN')} ${vi.chiTiet.kb}`
                : vi.chiTiet.chuaCo}
            </dd>
            <dt>{vi.chiTiet.viTri}</dt>
            <dd>
              {tb.viDo !== null && tb.kinhDo !== null ? (
                <>
                  {tb.viDo.toFixed(5)} , {tb.kinhDo.toFixed(5)}{' '}
                  <Link to={`/thiet-bi/ban-do?tam=${tb.viDo},${tb.kinhDo}`}>{vi.chiTiet.xemTrenBanDo}</Link>
                </>
              ) : (
                vi.chiTiet.chuaCo
              )}
            </dd>
          </dl>
          {/* Thiết bị gián tiếp: nêu rõ đường đi của lệnh ngay ở tab thông tin. */}
          {tb.phuongThucKetNoi === 'gian_tiep' ? (
            <p className="dai-thong-bao" role="note">
              {vi.bieuMau.ghiChuGianTiep}
            </p>
          ) : null}
          {phien.coQuyen('device.credential') ? (
            <TaiKhoanKetNoiThietBi
              deviceId={tb.id}
              cumLoaId={tb.cumLoaId}
              phuongThucKetNoi={tb.phuongThucKetNoi}
            />
          ) : null}
        </div>
      ) : null}

      {tab === 'trangThai' ? <TabTrangThai id={id} /> : null}
      {tab === 'nhatKy' ? <TabNhatKy id={id} /> : null}
      {tab === 'baoTri' ? <TabBaoTri id={id} /> : null}

      {moNgan ? (
        <MH08NganDieuKhien
          thietBi={[tb]}
          onDong={() => {
            datMoNgan(false);
            kq.taiLai();
          }}
        />
      ) : null}
    </section>
  );
}

/** Tab trạng thái: biểu đồ thời gian, nêu rõ độ phân giải dữ liệu. */
function TabTrangThai({ id }: { id: string }): ReactNode {
  const [khoang, datKhoang] = useState<(typeof KHOANG)[number]['ma']>('24h');
  const chon = KHOANG.find((k) => k.ma === khoang) ?? KHOANG[0];
  const den = Math.floor(Date.now() / 1000);

  const kq = dungTaiDuLieu<{ mucDo: 'tho' | 'gio' | 'ngay'; diem: Record<string, unknown>[] }>({
    tai: (signal) =>
      thietBiApi.lichSuTrangThai(id, { tuThoiDiem: den - chon.giay, denThoiDiem: den }, signal),
    laRong: (d) => d.diem.length === 0,
    // Khoảng thời gian là điều kiện lọc, nên rỗng ở đây là "rỗng vì bộ lọc".
    dangLoc: true,
    phuThuoc: [id, khoang],
  });

  const nhanMucDo =
    kq.duLieu?.mucDo === 'ngay'
      ? vi.chiTiet.doPhanGiaiNgay
      : kq.duLieu?.mucDo === 'gio'
        ? vi.chiTiet.doPhanGiaiGio
        : vi.chiTiet.doPhanGiaiTho;

  const canh = 120;
  const diem = kq.duLieu?.diem ?? [];
  const amLuong = diem.map((d) => Number(d.am_luong ?? 0));
  const toiDa = Math.max(100, ...amLuong);

  return (
    <div role="tabpanel" id="bang-trangThai" aria-labelledby="tab-trangThai">
      <p className="truong">
        <label htmlFor="ct-khoang">{vi.chiTiet.khoangThoiGian}</label>
        <select
          id="ct-khoang"
          value={khoang}
          onChange={(e) => datKhoang(e.target.value as (typeof KHOANG)[number]['ma'])}
        >
          {KHOANG.map((k) => (
            <option key={k.ma} value={k.ma}>
              {k.nhan}
            </option>
          ))}
        </select>
      </p>
      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={4} soCot={1} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong /> : null}
      {kq.trangThai === 'rong-loc' ? <TrangThaiRongLoc onXoaLoc={() => datKhoang('90d')} /> : null}
      {kq.trangThai === 'co-du-lieu' ? (
        <>
          <p className="chu-thich">
            {vi.chiTiet.doPhanGiai}: <strong>{nhanMucDo}</strong>
          </p>
          <svg
            className="bieu-do"
            viewBox={`0 0 ${Math.max(100, diem.length * 6)} ${canh}`}
            role="img"
            aria-label={`${vi.chiTiet.amLuong} — ${nhanMucDo}`}
          >
            <polyline
              fill="none"
              stroke="var(--mau-chu-dao)"
              strokeWidth="2"
              points={amLuong.map((v, i) => `${i * 6},${canh - (v / toiDa) * (canh - 10)}`).join(' ')}
            />
          </svg>
          {/* Biểu đồ luôn kèm bảng số: đường vẽ không phải phương tiện duy nhất. */}
          <table className="bang">
            <thead>
              <tr>
                <th scope="col">{vi.bang.capNhatLuc}</th>
                <th scope="col">{vi.chiTiet.amLuong}</th>
                <th scope="col">{vi.chiTiet.nhietDo}</th>
              </tr>
            </thead>
            <tbody>
              {diem.slice(-20).map((d, i) => (
                <tr key={i}>
                  <td>{String(d.moc ?? '')}</td>
                  <td>{String(d.am_luong ?? '—')}</td>
                  <td>{String(d.nhiet_do ?? '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}

/** Tab nhật ký: ba loại nhật ký, tra cứu được theo mã gói tin. */
function TabNhatKy({ id }: { id: string }): ReactNode {
  const [loai, datLoai] = useState('dieu_khien');
  const [goiTinId, datGoiTinId] = useState('');

  const kq = dungTaiDuLieu<{ items: Record<string, unknown>[]; total: number }>({
    tai: (signal) => thietBiApi.nhatKy(id, { loai, goiTinId: goiTinId || undefined, pageSize: 20 }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc: goiTinId.length > 0,
    phuThuoc: [id, loai, goiTinId],
  });

  return (
    <div role="tabpanel" id="bang-nhatKy" aria-labelledby="tab-nhatKy">
      <p className="truong">
        <label htmlFor="nk-loai">{vi.chiTiet.loaiNhatKy}</label>
        <select id="nk-loai" value={loai} onChange={(e) => datLoai(e.target.value)}>
          <option value="dieu_khien">{vi.chiTiet.nhatKyDieuKhien}</option>
          <option value="phat">{vi.chiTiet.nhatKyPhat}</option>
          <option value="lich_phat">{vi.chiTiet.nhatKyLichPhat}</option>
        </select>
        <label htmlFor="nk-goi-tin">{vi.chiTiet.traTheoGoiTin}</label>
        <input id="nk-goi-tin" type="search" value={goiTinId} onChange={(e) => datGoiTinId(e.target.value)} />
      </p>
      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={5} soCot={4} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.trangThaiManHinh.rongMoTa} /> : null}
      {kq.trangThai === 'rong-loc' ? <TrangThaiRongLoc onXoaLoc={() => datGoiTinId('')} /> : null}
      {kq.trangThai === 'co-du-lieu' ? (
        <table className="bang">
          <thead>
            <tr>
              {Object.keys(kq.duLieu?.items[0] ?? {}).map((k) => (
                <th scope="col" key={k}>
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(kq.duLieu?.items ?? []).map((d, i) => (
              <tr key={i}>
                {Object.keys(kq.duLieu?.items[0] ?? {}).map((k) => (
                  <td key={k}>{d[k] === null || d[k] === undefined ? '—' : String(d[k])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** Tab bảo trì: MH-10 đầy đủ thuộc đợt sau; bản này hiển thị sự kiện suy được. */
function TabBaoTri({ id }: { id: string }): ReactNode {
  const kq = dungTaiDuLieu<{ suKien: Record<string, unknown>[]; thieuBang: boolean }>({
    tai: (signal) => thietBiApi.baoTri(id, signal),
    laRong: (d) => d.suKien.length === 0,
    dangLoc: false,
    phuThuoc: [id],
  });

  return (
    <div role="tabpanel" id="bang-baoTri" aria-labelledby="tab-baoTri">
      <p className="dai-thong-bao" role="note">
        {vi.chiTiet.baoTriThieuBang}
      </p>
      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={3} soCot={3} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.chiTiet.baoTriThieuBang} /> : null}
      {kq.trangThai === 'rong-loc' ? <TrangThaiRongLoc onXoaLoc={kq.taiLai} /> : null}
      {kq.trangThai === 'co-du-lieu' ? (
        <ul className="dong-thoi-gian">
          {(kq.duLieu?.suKien ?? []).map((s, i) => (
            <li key={i}>
              <strong>{vi.chiTiet.suKien[s.loai as keyof typeof vi.chiTiet.suKien] ?? String(s.loai)}</strong>{' '}
              — {s.thoiDiem ? new Date(Number(s.thoiDiem) * 1000).toLocaleString('vi-VN') : '—'}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
